/**
 * ProjectNew — nieuw dossier in twee stappen; tekeningen op de projectpagina.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProjectFlowVisual, ProjectJourneySteps } from '../components/ProjectJourneySteps';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { trackProjectCreated } from '../lib/analytics';
import { CUSTOMER_TYPE_OPTIONS, DEFAULT_CALC_INDEX_YEAR } from '../lib/customer-types';
import { getJourneyProfileHint } from '../lib/project-flow-copy';

function normalizePostalCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

export function ProjectNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectTypeId, setProjectTypeId] = useState('renovatie');
  const [buildingTypeId, setBuildingTypeId] = useState('woning');
  const [postalCode, setPostalCode] = useState('');
  const [customerTypeId, setCustomerTypeId] = useState<string>('particulier');
  const [projectTypeOptions, setProjectTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'renovatie', label: 'Renovatie' },
  ]);
  const [buildingTypeOptions, setBuildingTypeOptions] = useState<Array<{ id: string; label: string }>>([
    { id: 'woning', label: 'Woning' },
  ]);
  const [typesLoadError, setTypesLoadError] = useState<string | null>(null);
  const [typesLoading, setTypesLoading] = useState(true);

  const loadReferenceTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesLoadError(null);
    try {
      const [pt, bt] = await Promise.all([api.getProjectTypes(), api.getBuildingTypes()]);
      if (pt.project_types?.length) setProjectTypeOptions(pt.project_types);
      if (bt.building_types?.length) setBuildingTypeOptions(bt.building_types);
    } catch {
      setTypesLoadError('Project- en gebouwtypes konden niet worden geladen. Controleer je verbinding.');
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferenceTypes();
  }, [loadReferenceTypes]);

  const projectTypeLabel = useMemo(
    () => projectTypeOptions.find((o) => o.id === projectTypeId)?.label ?? projectTypeId,
    [projectTypeOptions, projectTypeId]
  );
  const buildingTypeLabel = useMemo(
    () => buildingTypeOptions.find((o) => o.id === buildingTypeId)?.label ?? buildingTypeId,
    [buildingTypeOptions, buildingTypeId]
  );
  const customerTypeLabel = useMemo(
    () => CUSTOMER_TYPE_OPTIONS.find((o) => o.id === customerTypeId)?.label ?? customerTypeId,
    [customerTypeId]
  );

  const profileHint = useMemo(
    () =>
      getJourneyProfileHint({
        projectTypeId,
        buildingTypeId,
        customerTypeId,
        projectTypeLabel,
        buildingTypeLabel,
        customerTypeLabel,
      }),
    [
      projectTypeId,
      buildingTypeId,
      customerTypeId,
      projectTypeLabel,
      buildingTypeLabel,
      customerTypeLabel,
    ]
  );

  const goStep2 = () => {
    if (!projectName.trim()) {
      setError('Vul een projectnaam in');
      return;
    }
    if (!customerTypeId) {
      setError('Kies een klanttype');
      return;
    }
    setError(null);
    setWizardStep(2);
  };

  const handleCreate = async () => {
    if (!projectName.trim()) {
      setError('Projectnaam is verplicht');
      return;
    }
    if (!customerTypeId) {
      setError('Kies een klanttype');
      return;
    }
    const pc = normalizePostalCode(postalCode);
    if (pc && !/^\d{4}[A-Z]{2}$/.test(pc)) {
      setError('Postcode ongeldig (gebruik formaat 1234AB)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let regionId: string | undefined;
      if (pc && /^\d{4}[A-Z]{2}$/.test(pc)) {
        try {
          const s = await api.suggestRegionFromPostalCode(pc);
          regionId = s.region_id;
        } catch {
          /* dossier zonder regio; gebruiker vult later aan */
        }
      }

      const { project_id } = await api.createProject({
        systems: [],
        metadata: { name: projectName.trim() },
        project_type: projectTypeId,
        building_type: buildingTypeId,
        tenant_id: tenantId,
        context: {
          customer_type: customerTypeId,
          postal_code: pc || undefined,
          index_year: DEFAULT_CALC_INDEX_YEAR,
          ...(regionId ? { region_id: regionId } : {}),
        },
      });

      trackProjectCreated(project_id);
      navigate(`/projects/${project_id}/gegevens`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Er is iets misgegaan');
    } finally {
      setLoading(false);
    }
  };

  const primaryBtnClass =
    'rounded-lg bg-primary px-6 py-3 font-medium text-white shadow-md transition hover:bg-primary-alt disabled:opacity-50';

  const step1Footer = (
    <button type="button" onClick={goStep2} className={`w-full md:w-auto ${primaryBtnClass}`}>
      Verder
    </button>
  );

  const step2Footer = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setWizardStep(1);
        }}
        disabled={loading}
        className="w-full rounded-lg border border-border bg-surface-card px-6 py-3 font-medium text-text transition hover:bg-surface disabled:opacity-50 sm:w-auto"
      >
        Terug
      </button>
      <button
        type="button"
        onClick={() => void handleCreate()}
        disabled={loading || typesLoading}
        className={`w-full sm:w-auto ${primaryBtnClass}`}
      >
        {loading ? 'Bezig...' : 'Dossier aanmaken en verder'}
      </button>
    </div>
  );

  return (
    <div className="animate-page-enter pb-28 md:pb-10">
      <SeoMeta
        path="/projects/new"
        noindex
        title="Nieuw dossier"
        description="Start een nieuw calculatiedossier in CalqTool™: kies template of leeg, werk daarna verder met posten, resultaat en export. Prijsboek, documenten en optionele tekeninganalyse."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Nieuw dossier' }]} />
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-muted">
          {wizardStep === 1 ? 'Stap 1 van 2' : 'Stap 2 van 2'}
        </p>
        <h1 className="mb-2 text-2xl font-bold text-text">Start een dossier</h1>
        <p className="mb-8 max-w-xl text-sm text-text-muted">
          Daarna werk je op de projectpagina in <strong className="text-text">vier tabs</strong>: Gegevens, Posten
          (regels opslaan), Resultaat en Rapportage. Optioneel op Gegevens: tekening of PDF analyseren (nieuwe upload of
          platen uit de documentenbibliotheek); posten uit de analyse kun je <strong className="text-text">in delen</strong>{' '}
          importeren en op Posten pas goedkeuren als je klaar bent.
        </p>
        <p className="mb-6 max-w-xl text-sm text-text-muted">
          Liever begeleid starten met vragen en een automatisch voorstel? Ga naar{' '}
          <Link to="/projects/intake" className="font-medium text-primary hover:underline">
            Begeleid nieuw dossier
          </Link>
          .
        </p>

        {wizardStep === 1 && (
          <div className="mb-8 max-w-md space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Projectnaam</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Bijv. Badkamer renovatie Janssen"
                className="w-full rounded-lg border border-border px-3 py-2 text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text">Klanttype</label>
              <select
                value={customerTypeId}
                onChange={(e) => setCustomerTypeId(e.target.value)}
                required
                className="w-full rounded-lg border border-border px-3 py-2 text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {CUSTOMER_TYPE_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-muted">
                Zodat we het dossier en de vervolgstappen op jouw situatie afstemmen.
              </p>
            </div>
          </div>
        )}

        {wizardStep === 2 && (
          <>
            <ProjectFlowVisual
              variant="empty"
              tagline="Je vult nu type, gebouw en locatie in — daarna maak je het dossier aan."
              className="mb-6"
            />

            {typesLoadError && (
              <div
                className="mb-4 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between"
                role="alert"
              >
                <span>{typesLoadError}</span>
                <button
                  type="button"
                  onClick={() => void loadReferenceTypes()}
                  className="shrink-0 rounded-md bg-amber-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-900 dark:bg-amber-700 dark:hover:bg-amber-600"
                >
                  Opnieuw proberen
                </button>
              </div>
            )}

            <div className="mb-6 grid max-w-2xl gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Projecttype</label>
                <select
                  value={projectTypeId}
                  onChange={(e) => setProjectTypeId(e.target.value)}
                  disabled={typesLoading}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                >
                  {projectTypeOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text">Gebouwtype</label>
                <select
                  value={buildingTypeId}
                  onChange={(e) => setBuildingTypeId(e.target.value)}
                  disabled={typesLoading}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                >
                  {buildingTypeOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-text">Postcode</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="1234 AB (aanbevolen)"
                  autoComplete="postal-code"
                  className="w-full max-w-xs rounded-lg border border-border px-3 py-2 font-mono text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Basis voor locatie; later te koppelen aan regio, ESG, subsidies, WOZ e.d.
                </p>
              </div>
            </div>

            <ProjectJourneySteps
              variant="empty"
              currentStep={1}
              profileHint={profileHint}
              className="mb-8"
            />
          </>
        )}

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="mb-8 hidden md:block">{wizardStep === 1 ? step1Footer : step2Footer}</div>
      </AnimatedSection>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-surface-card/95 p-3 shadow-[0_-4px_24px_rgba(0,0,0,0.12)] backdrop-blur-md md:hidden">
        <div className="mx-auto max-w-lg">{wizardStep === 1 ? step1Footer : step2Footer}</div>
      </div>
    </div>
  );
}
